import {
  type Dispatch,
  type SetStateAction,
  createContext,
  useEffect,
  useState,
} from "react";
import { Outlet, useOutletContext } from "react-router-dom";

import { SpinnerLoader } from "components/loader/loader";
import { type KeystoneOfferEligibility } from "data/keystone-api-mapping";
import { useClaimsConstants } from "data/ss-data-hooks";
import { useMultiStepFormState } from "hooks/use-multi-step-form-state";
import { useHideSupportChat } from "utils";

import styles from "./shingle-reseal.module.scss";

export type WindDefenseContextState = {
  firstName?: string;
  lastName?: string;
  emailAddress?: string;
  phone?: string;
  termsSageSure?: string;
  termsLiftLock?: string;
  termsSageSureAgreed?: boolean;
  termsLiftLockAgreed?: boolean;
  offeringId?: string;
  offerId?: string;
} | null;

export const WindDefenseContext = createContext<{
  state: WindDefenseContextState;
  setState: Dispatch<SetStateAction<WindDefenseContextState>>;
}>({ state: null, setState: () => null });

export const WindDefenseChecker = () => {
  useHideSupportChat();
  const { setFields, state } =
    useMultiStepFormState<WindDefenseContextState>(WindDefenseContext);
  const { data: claimsConstants, isLoading } = useClaimsConstants();
  const policyHolderPhone = claimsConstants?.PolicyHolderDetails.phoneNumber;

  const eligibilityData = useOutletContext<KeystoneOfferEligibility>();

  useEffect(() => {
    const termsSageSure = eligibilityData?.additionalDetails.find(
      (detail) => detail.assetTypeCode === "INTERNAL_TERMS_OF_USE",
    );
    const termsLiftLock = eligibilityData?.additionalDetails.find(
      (detail) => detail.assetTypeCode === "PROVIDER_TERMS_OF_USE",
    );
    setFields({
      termsSageSure: termsSageSure?.assetTextContent,
      termsLiftLock: termsLiftLock?.assetTextContent,
    });
  }, [eligibilityData?.additionalDetails, setFields]);

  useEffect(() => {
    if (eligibilityData?.offeringId) {
      setFields({ offeringId: eligibilityData.offeringId });
    }
  }, [eligibilityData?.offeringId, setFields]);

  useEffect(() => {
    if (eligibilityData?.offerId) {
      setFields({ offerId: eligibilityData.offerId });
    }
  }, [eligibilityData?.offerId, setFields]);

  useEffect(() => {
    if (policyHolderPhone && !state?.phone) {
      setFields({ phone: policyHolderPhone ?? "" });
    }
  }, [policyHolderPhone, setFields, state?.phone]);

  if (isLoading) {
    return (
      <div className={styles.loaderWrapper}>
        <SpinnerLoader />
      </div>
    );
  }

  return <Outlet />;
};

export const WindDefenseFlow = () => {
  const [state, setState] = useState<WindDefenseContextState>(null);
  return (
    <WindDefenseContext.Provider value={{ state, setState }}>
      <WindDefenseChecker />
    </WindDefenseContext.Provider>
  );
};
